import styled from '@emotion/styled';
import {
  defaultContainerSize,
  mq,
  pseudoElementDefaultContent,
} from '../../../styles/mixins';
import { THEME } from '../../../common/consts/theme';
import { rgba } from 'emotion-rgba';
import { css } from '@emotion/react';
import Link from 'next/link';
import {
  Headline4,
  ParagraphS,
  ParagraphXs,
} from '../../core/Typography/Typography';

type ArticleTagItemType = {
  isActive: boolean;
};

export const ArticlesListSection = styled.section`
  display: flex;
  flex-direction: column;
  align-items: center;
  margin-bottom: 5rem;

  ${mq.xLarge(css`
    width: 568px;
    padding: 0;
    margin-bottom: 7.5rem;
  `)};
`;

export const ContentWrapper = styled.div`
  width: 100%
`

export const ArticleTagsList = styled.ul`
  ${defaultContainerSize};
  max-width: 73rem;
  display: flex;
  flex-direction: row;
  justify-content: flex-start;
  align-items: center;
  list-style: none;
  padding-top: 1rem;
  padding-bottom: 1rem;
  font-family: ${({ theme }) => theme.font.questrial};
  font-style: normal;
  font-weight: normal;
  font-size: 1rem;
  line-height: 2rem;
`;

export const ArticleTagItem = styled.li<ArticleTagItemType>`
  font-family: ${({ theme }) => theme.font.questrial};
  cursor: pointer;
  display: inline-block;
  padding: 0.3125rem 1rem;
  white-space: nowrap;
  background: linear-gradient(
    22.45deg,
    ${THEME.colors.primary} 3.28%,
    ${THEME.colors.accent} 102.02%
  );
  background-clip: text;
  color: ${({ theme }) => theme.colors.darker_grey};
  transition: color 200ms ease-in-out;

  &:hover {
    color: ${rgba(THEME.colors.darker_grey, 0)};
  }

  ${({ isActive }) =>
    isActive &&
    css`
      border: 0.0625rem solid ${THEME.colors.darker_grey};
      box-sizing: border-box;
      border-radius: 3.75rem;

      &:hover {
        color: ${rgba(THEME.colors.darker_grey, 1)};
      }
    `};
`;

export const ArticleShareWrapper = styled.div`
  display: flex;
  flex-direction: row;
  max-height: 3rem;
  max-width: unset;

  ${mq.medium(css`
    margin-left: 2.5em;
  `)};

  ${mq.xLarge(css`
    position: sticky;
    top: 10rem;
    margin-top: 19rem;
    margin-bottom: 11rem;
    max-height: 11.4rem;
    max-width: 3rem;
    flex-direction: column;
    margin-left: 0;
  `)};
`;

export const ArticleShareIcon = styled(Link)`
  width: 3rem;
  height: 3rem;
  position: relative;
  border: 0.0625rem solid ${({ theme }) => theme.colors.grey};
  border-radius: 50%;
  transition: box-shadow 200ms ease-in-out;

  svg {
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
  }

  &:hover {
    box-shadow: 0.5rem 0.5rem 1.5rem 0 #1700594a;
  }

  &:not(:first-of-type) {
    margin-left: 1rem;

    ${mq.xLarge(css`
      margin-left: 0;
      margin-top: 1rem;
    `)};
  }
`;

export const ArticleSummaryWrapper = styled.div`
  &:not(:last-child) {
    margin-bottom: 4rem;
  }

  ${mq.xLarge(css`
    &:not(:last-child) {
      margin-bottom: 3rem;
    }
  `)};
`;

export const ArticleSummaryContent = styled.div`
  margin: 1.5rem 0 0 0;
  font-family: ${({ theme }) => theme.font.nunito};
  font-weight: 300;
  font-size: 1rem;
  line-height: 2rem;
  letter-spacing: 0.013px;

  ${mq.xLarge(css`
    margin: 1rem 0 0 0;
  `)};
`;

export const ArticleSummaryReadMore = styled(Link)`
  position: relative;
  font-family: ${({ theme }) => theme.font.nunito};
  font-weight: 600;
  font-size: 1.125rem;
  line-height: 2rem;
  text-decoration: none;
  background: linear-gradient(
    22.45deg,
    ${THEME.colors.primary} 3.28%,
    ${THEME.colors.accent} 102.02%
  );
  background-clip: text;
  color: ${({ theme }) => theme.colors.core_black};
  transition: color 200ms ease-in-out;

  &:after {
    background: linear-gradient(
      67.33deg,
      ${THEME.colors.primary} -19.99%,
      ${THEME.colors.accent} 98.54%
    );
    transition: opacity 200ms ease-in-out;
    opacity: 0;
    ${pseudoElementDefaultContent};
  }

  &:before {
    background: ${({ theme }) => theme.colors.core_black};
    ${pseudoElementDefaultContent};
  }

  &:hover {
    color: ${rgba(THEME.colors.core_black, 0)};

    &:after {
      opacity: 1;
    }
  }
`;

export const ArticleHeaderLink = styled(Link)`
  ${Headline4};
  margin: 0 0 0.5rem 0;
  background: linear-gradient(
    22.45deg,
    ${THEME.colors.primary} 3.28%,
    ${THEME.colors.accent} 102.02%
  );
  background-clip: text;
  color: ${({ theme }) => theme.colors.black};
  text-decoration: none;
  transition: color 200ms ease-in-out;
  display: block;

  ${mq.medium(css`
    margin: 0 0 1rem 0;
  `)};

  ${mq.large(css`
    font-size: 2rem;
    line-height: 2.5rem;
  `)};

  &:hover {
    color: ${rgba(THEME.colors.black, 0)};
  }

  &:focus {
    outline: none;
  }
`;

export const ArticleHeaderLinkH1 = styled.h1`
  ${Headline4};
  margin: 0 0 0.5rem 0;
  background: linear-gradient(
    22.45deg,
    ${THEME.colors.primary} 3.28%,
    ${THEME.colors.accent} 102.02%
  );
  background-clip: text;
  color: ${({ theme }) => theme.colors.black};
  text-decoration: none;
  transition: color 200ms ease-in-out;
  display: block;

  ${mq.medium(css`
    margin: 0 0 1rem 0;
  `)};

  ${mq.large(css`
    font-size: 2rem;
    line-height: 2.5rem;
  `)};

  &:hover {
    color: ${rgba(THEME.colors.black, 0)};
  }

  &:focus {
    outline: none;
  }
`;

export const MetaName = styled.span`
  ${ParagraphS};
  font-family: ${({ theme }) => theme.font.questrial};
  font-weight: 400;
  letter-spacing: 0.0125rem;
`;

export const MetaDate = styled.span`
  ${ParagraphXs};
  font-weight: 400;
`;

export const MetaTagLink = styled(Link)`
  ${ParagraphXs};
  font-weight: 400;
  color: ${({ theme }) => theme.colors.darker_grey};
`;

export const ArticleParagraph = styled.p`
  ${ParagraphS};
  margin: 1.5rem 0;
  color: ${({ theme }) => theme.colors.black};
`;

export const ArticleOrderedList = styled.ol`
  color: ${({ theme }) => theme.colors.black};
  margin: 0.75rem 0;
  padding-left: 1.5rem;
  font-family: ${({ theme }) => theme.font.nunito};
  font-size: 1rem;
  line-height: 1.5rem;
  counter-reset: custom-counter;
  list-style: none outside none;
  position: relative;
`;

export const ArticleOrderedListItem = styled.li`
  color: ${({ theme }) => theme.colors.black};
  margin: 0.75rem 0;
  font-family: ${({ theme }) => theme.font.nunito};
  font-size: 1rem;
  line-height: 1.5rem;
  counter-increment: custom-counter;

  &:before {
    content: counter(custom-counter) ') ';
    color: ${({ theme }) => theme.colors.black};
    font-weight: bold;
    position: absolute;
    left: 0;
  }
`;

export const ArticleNextWrapper = styled.div`
  display: inline-flex;
  flex-wrap: nowrap;
  gap: 1rem;

  ${mq.medium(css`
    gap: 1.875rem;
  `)};
`;

export const ArticleNextMeta = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  max-width: 23.125rem;
`;

export const ArticleNextTitle = styled(Link)`
  position: relative;
  font-size: 1.125rem;
  line-height: 1.5rem;
  font-family: var(--font-base);
  text-decoration: none;
  font-weight: normal;
  background: linear-gradient(
    22.45deg,
    ${THEME.colors.primary} 3.28%,
    ${THEME.colors.accent} 102.02%
  );
  background-clip: text;
  color: ${({ theme }) => theme.colors.core_black};
  transition: color 200ms ease-in-out;
  cursor: pointer;

  &:hover {
    color: ${rgba(THEME.colors.dark_grey, 0)};
  }

  ${mq.xLarge(css`
    font-size: 1.5rem;
    line-height: 2rem;
  `)};
`;
